import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService, UserInfo } from 'angular-oauth2-oidc';
import { BehaviorSubject } from 'rxjs';
import { authConfig } from './auth.config';
import { User } from '../../models/user-info.model';

export interface IAuthService {
  /**
   * emits when a new token is received
   */
  onTokenReceived: EventEmitter<void>;
  /**
   * emits when an error occurs
   */
  onError: EventEmitter<void>;
  /**
   * Initialize authentication, this will either get the users current valid access token or redirect them to the auth sign in
   */
  initAuth(): void;
  /**
   * returns the current access_token
   */
  getAccessToken(): string;
  /**
   * checks if the current access_token is valid
   */
  hasValidAccessToken(): boolean;
}

/**
 * Provides a wrapper around a third party OAuth2 library and provides convenience methods for the authentication process
 */
@Injectable()
export class AuthService implements IAuthService {
  public onTokenReceived: EventEmitter<void>;
  public onError: EventEmitter<void>;
  private _user = new BehaviorSubject<User>({ emplid: '', email: '', firstName: '', lastName: '' });
  private _accessToken = new BehaviorSubject<string>('');

  get userInfo() {
    return this._user.asObservable();
  }

  constructor(private oAuthService: OAuthService, private http: HttpClient) {
    this.onTokenReceived = new EventEmitter<void>();
    this.onError = new EventEmitter<void>();
  }

  public initAuth(): void {
    this.subscribeToEvents();
    this.oAuthService.configure(authConfig);
    this.oAuthService
      .loadDiscoveryDocumentAndTryLogin()
      .then(() => {
        if (!this.hasValidAccessToken()) {
          this.oAuthService.initCodeFlow();
        }
      })
      .catch(() => this.onError.emit());
    this.oAuthService.setupAutomaticSilentRefresh();
  }

  public getAccessToken(): string {
    return this.oAuthService.getAccessToken();
  }

  public hasValidAccessToken(): boolean {
    return this.oAuthService.hasValidAccessToken();
  }

  public updateUserInfo(): void {
    if (localStorage['id_token_claims_obj'] !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument
      const user: Partial<UserInfo> = JSON.parse(localStorage['id_token_claims_obj']);
      this._user.next({
        emplid: user['emplid'] as string,
        email: user['email'] as string,
        firstName: user['given_name'] as string,
        lastName: user['family_name'] as string,
      });
      this._accessToken.next(this.getAccessToken());
    }
  }

  /**
   * subscribe to OAuthService events and then translate and emit them from the Auth Service
   */
  private subscribeToEvents(): void {
    this.oAuthService.events.subscribe((event) => {
      if (event.type === 'token_received') {
        this.onTokenReceived.emit();
        // pass the access token that was just received to the userInfo endpoint
        // which will return the claims for the authenticated user
        void this.oAuthService.loadUserProfile();
        console.log(this.oAuthService.getIdentityClaims());
      } else if (event.type === 'user_profile_loaded') {
        this.onTokenReceived.emit();
        this.updateUserInfo();
      }
      if (event.type.includes('_error')) {
        this.onError.emit();
      }
    });
  }
}
